import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";

/**
 * SpellCaster — a spellbook command bar.
 *
 * Press "/" (or Cmd/Ctrl-K) to raise the wand, type an incantation, Enter to
 * cast. Spells that other components care about are broadcast as window
 * CustomEvents named `cast:<spell>`.
 */

type Cast = { message: string; ok: boolean };

const ACCIO_TARGETS: Record<string, string> = {
  home: "/", "great hall": "/", hero: "/",
  skills: "/skills", grounds: "/skills",
  experience: "/experience", express: "/experience", train: "/experience",
  projects: "/projects", spells: "/projects", "restricted section": "/projects",
  leetcode: "/leetcode", duel: "/leetcode", "duelling club": "/leetcode",
  opensource: "/opensource", "open source": "/opensource", forest: "/opensource",
  education: "/education", owls: "/education",
  contact: "/contact", owlery: "/contact", owl: "/contact",
};

export const SPELLBOOK = [
  { incantation: "lumos", effect: "Light the castle" },
  { incantation: "nox", effect: "Put out the light" },
  { incantation: "accio <place>", effect: "Summon a page" },
  { incantation: "revelio", effect: "Reveal what is hidden" },
  { incantation: "alohomora", effect: "Unlock the sealed card" },
  { incantation: "expecto patronum", effect: "Cast a Patronus" },
  { incantation: "obliviate", effect: "Forget all charms" },
];

const SpellCaster: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [cast, setCast] = useState<Cast | null>(null);
  const [light, setLight] = useState<"lumos" | "nox" | null>(null);
  const [patronus, setPatronus] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  /* ---- open / close ---- */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);

      if (!open && !typing && (e.key === "/" || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k"))) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (open && e.key === "Escape") {
        setOpen(false);
        setValue("");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  /* ---- the toast fades on its own ---- */
  useEffect(() => {
    if (!cast) return;
    const id = window.setTimeout(() => setCast(null), 2600);
    return () => window.clearTimeout(id);
  }, [cast]);

  useEffect(() => {
    if (!patronus) return;
    const id = window.setTimeout(() => setPatronus(false), 2800);
    return () => window.clearTimeout(id);
  }, [patronus]);

  const perform = useCallback((raw: string) => {
    const spell = raw.trim().toLowerCase().replace(/\s+/g, " ");
    if (!spell) return;

    window.dispatchEvent(new CustomEvent(`cast:${spell.split(" ")[0]}`, { detail: spell }));

    if (spell === "lumos") {
      setLight("lumos");
      return setCast({ message: "Lumos — the castle brightens.", ok: true });
    }
    if (spell === "nox") {
      setLight("nox");
      return setCast({ message: "Nox — the light goes out.", ok: true });
    }
    if (spell === "revelio") {
      document.documentElement.setAttribute("data-revelio", "on");
      window.setTimeout(() => document.documentElement.removeAttribute("data-revelio"), 6000);
      return setCast({ message: "Revelio — hidden things surface.", ok: true });
    }
    if (spell === "alohomora") {
      return setCast({ message: "Alohomora — the seal breaks open.", ok: true });
    }
    if (spell === "expecto patronum") {
      setPatronus(true);
      return setCast({ message: "Expecto Patronum — a stag takes shape.", ok: true });
    }
    if (spell === "obliviate") {
      setLight(null);
      document.documentElement.removeAttribute("data-revelio");
      return setCast({ message: "Obliviate — every charm forgotten.", ok: true });
    }
    if (spell.startsWith("accio")) {
      const target = spell.replace("accio", "").trim();
      const path = ACCIO_TARGETS[target];
      if (path) {
        navigate(path);
        return setCast({ message: `Accio ${target} — summoned.`, ok: true });
      }
      return setCast({ message: `Nothing answers to “${target || "…"}”.`, ok: false });
    }

    setCast({ message: `“${spell}” is not in this spellbook.`, ok: false });
  }, [navigate]);

  const submit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    perform(value);
    setValue("");
    setOpen(false);
  }, [value, perform]);

  return (
    <>
      {/* Lumos / Nox wash over the whole page */}
      {light && <LightWash $mode={light} aria-hidden="true" />}

      {patronus && (
        <PatronusLayer aria-hidden="true">
          <Stag>&#129420;</Stag>
        </PatronusLayer>
      )}

      <WandTrigger onClick={() => setOpen(true)} aria-label="Cast a spell">
        <TriggerGlyph>&#10024;</TriggerGlyph>
        <TriggerText>Cast</TriggerText>
      </WandTrigger>

      {open && (
        <Backdrop onClick={() => { setOpen(false); setValue(""); }}>
          <Book onClick={(e) => e.stopPropagation()}>
            <BookTitle>The Standard Book of Spells</BookTitle>
            <form onSubmit={submit}>
              <SpellInput
                ref={inputRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Speak the incantation&hellip;"
                spellCheck={false}
                autoComplete="off"
              />
            </form>
            <SpellList>
              {SPELLBOOK.map((s) => (
                <SpellRow key={s.incantation} onClick={() => { perform(s.incantation.replace(" <place>", " projects")); setOpen(false); }}>
                  <Incant>{s.incantation}</Incant>
                  <Effect>{s.effect}</Effect>
                </SpellRow>
              ))}
            </SpellList>
            <Footnote>Enter to cast &middot; Esc to close the book</Footnote>
          </Book>
        </Backdrop>
      )}

      {cast && <Toast $ok={cast.ok}>{cast.message}</Toast>}
    </>
  );
};

export default SpellCaster;

/* ==================== styles ==================== */

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const toastIn = keyframes`
  0%   { opacity: 0; transform: translate(-50%, 14px); }
  12%  { opacity: 1; transform: translate(-50%, 0); }
  85%  { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -8px); }
`;

const stagRun = keyframes`
  0%   { opacity: 0; transform: translate(-30vw, 12vh) scale(0.6); }
  20%  { opacity: 1; }
  80%  { opacity: 1; }
  100% { opacity: 0; transform: translate(35vw, -18vh) scale(1.5); }
`;

const LightWash = styled.div<{ $mode: "lumos" | "nox" }>`
  position: fixed;
  inset: 0;
  z-index: 400;
  pointer-events: none;
  animation: ${fadeIn} 0.7s ease both;
  ${({ $mode }) => $mode === "lumos"
    ? css`background: radial-gradient(circle at 50% 40%, rgba(255, 236, 190, 0.20), rgba(255, 226, 160, 0.07) 45%, transparent 75%);`
    : css`background: rgba(2, 1, 6, 0.62);`}
`;

const PatronusLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 420;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Stag = styled.div`
  font-size: 5rem;
  filter: drop-shadow(0 0 26px rgba(180, 220, 255, 0.95)) brightness(2.2) saturate(0.15);
  animation: ${stagRun} 2.8s cubic-bezier(0.4, 0, 0.5, 1) both;
`;

const WandTrigger = styled.button`
  position: fixed;
  right: 1.4rem;
  bottom: 4.4rem;
  z-index: 120;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.85rem;
  border-radius: 100px;
  border: 1px solid rgba(155, 127, 212, 0.4);
  background: rgba(18, 12, 26, 0.82);
  backdrop-filter: blur(10px);
  color: #c3aef0;
  font-family: "Cinzel", serif;
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  cursor: none;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(155, 127, 212, 0.85);
    box-shadow: 0 0 22px rgba(155, 127, 212, 0.28);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) { right: 0.8rem; bottom: 3.6rem; padding: 0.4rem 0.65rem; }
`;

const TriggerGlyph = styled.span`font-size: 0.8rem; line-height: 1;`;
const TriggerText = styled.span`
  @media (max-width: 480px) { display: none; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 520;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 12vh 1.5rem 2rem;
  background: rgba(4, 2, 8, 0.78);
  backdrop-filter: blur(5px);
  animation: ${fadeIn} 0.25s ease both;
`;

const Book = styled.div`
  width: min(540px, 100%);
  padding: 1.5rem 1.6rem 1.1rem;
  border-radius: 6px;
  border: 1px solid rgba(196, 148, 42, 0.4);
  background: linear-gradient(160deg, rgba(28, 20, 38, 0.97), rgba(16, 11, 22, 0.97));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.75), inset 0 0 40px rgba(155, 127, 212, 0.06);
  animation: ${riseIn} 0.35s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

const BookTitle = styled.p`
  font-family: "Cinzel", serif;
  font-size: 0.6rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #8a7ab0;
  text-align: center;
  margin-bottom: 0.9rem;
`;

const SpellInput = styled.input`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 4px;
  border: 1px solid rgba(196, 148, 42, 0.28);
  background: rgba(0, 0, 0, 0.35);
  color: #f0e6dc;
  font-family: "Cinzel", serif;
  font-size: 1rem;
  letter-spacing: 0.05em;
  outline: none;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;

  &::placeholder { color: #6a5a7a; }
  &:focus {
    border-color: rgba(196, 148, 42, 0.7);
    box-shadow: 0 0 20px rgba(196, 148, 42, 0.16);
  }
`;

const SpellList = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  max-height: 40vh;
  overflow-y: auto;
`;

const SpellRow = styled.button`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 0.42rem 0.55rem;
  border: none;
  border-radius: 3px;
  background: transparent;
  text-align: left;
  cursor: none;
  transition: background 0.2s ease;

  &:hover { background: rgba(155, 127, 212, 0.1); }
`;

const Incant = styled.span`
  font-family: "Cinzel", serif;
  font-size: 0.8rem;
  color: #e2cf9a;
`;

const Effect = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 0.65rem;
  color: #8a7a72;
  text-align: right;
`;

const Footnote = styled.p`
  margin-top: 0.8rem;
  text-align: center;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.5rem;
  letter-spacing: 0.1em;
  color: #5a4a6a;
`;

const Toast = styled.div<{ $ok: boolean }>`
  position: fixed;
  left: 50%;
  bottom: 8rem;
  z-index: 530;
  transform: translateX(-50%);
  padding: 0.6rem 1.2rem;
  border-radius: 100px;
  font-family: "Cinzel", serif;
  font-size: 0.72rem;
  white-space: nowrap;
  pointer-events: none;
  color: ${({ $ok }) => ($ok ? "#e2cf9a" : "#c88a8a")};
  border: 1px solid ${({ $ok }) => ($ok ? "rgba(196,148,42,0.45)" : "rgba(200,120,120,0.4)")};
  background: rgba(14, 9, 20, 0.92);
  backdrop-filter: blur(8px);
  animation: ${toastIn} 2.6s ease both;
`;
